import React, { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, Pencil, Search, X } from 'lucide-react';
import { filterLines, formatSigned } from './stockTakeLogic';

// Missing lines first, then short, over, and fully counted last - so what still needs
// counting is always at the top.
const STATUS_ORDER = { not_scanned: 0, partial: 1, over: 2, complete: 3 };
const STATUS_LABEL = { not_scanned: 'Missing', partial: 'Short', over: 'Over', complete: 'Counted' };

// IC document lines. Tapping a row (or its Edit button) opens the count correction
// dialog. `highlight` ({ barcode, key }) scrolls to and flashes the scanned line; a new
// key replays it. Below 768px the CSS turns each row into a card.
const ICDocumentTable = ({ items, onEditLine, highlight }) => {
  const searchId = useId();
  const [query, setQuery] = useState('');
  const rowRefs = useRef(new Map());

  const sorted = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (STATUS_ORDER[a.item.status] - STATUS_ORDER[b.item.status]) || (a.index - b.index))
    .map(({ item }) => item);
  const visible = filterLines(sorted, query);

  // A scanned line hidden by the filter would be invisible; clear the filter for it
  const highlightHidden = Boolean(highlight && query && !visible.some((l) => l.barcode === highlight.barcode));
  useEffect(() => {
    if (highlightHidden) setQuery('');
  }, [highlightHidden]);

  useEffect(() => {
    if (!highlight) return;
    const row = rowRefs.current.get(highlight.barcode);
    if (row && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlight]);

  return (
    <div className="st-ic-lines">
      <div className="st-search">
        <label htmlFor={searchId} className="sr-only">Filter document lines</label>
        <Search size={18} className="st-search-icon" aria-hidden="true" />
        <input
          id={searchId}
          type="search"
          className="st-search-input"
          placeholder="Filter by description or barcode"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
        {query && (
          <button type="button" className="st-search-clear" onClick={() => setQuery('')} aria-label="Clear filter">
            <X size={18} aria-hidden="true" />
          </button>
        )}
      </div>
      {query && (
        <p className="st-filter-count" aria-live="polite">
          Showing {visible.length} of {items.length} lines
        </p>
      )}

      <div className="scanned-items-wrapper ic-doc-items">
        <table className="scanned-items-table ic-doc-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Description</th>
              <th>Barcode</th>
              <th className="st-num-col">Expected</th>
              <th className="st-num-col">Counted</th>
              <th className="st-num-col">Diff</th>
              <th><span className="sr-only">Edit</span></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((item) => {
              const name = item.description || item.partName || item.barcode;
              const isHighlighted = highlight && highlight.barcode === item.barcode;
              return (
                <tr
                  key={`${item.kline ?? ''}-${item.barcode}${isHighlighted ? `-${highlight.key}` : ''}`}
                  ref={(el) => {
                    if (el) rowRefs.current.set(item.barcode, el);
                    else rowRefs.current.delete(item.barcode);
                  }}
                  className={`ic-row ic-row-${item.status} ${isHighlighted ? 'latest-scan' : ''}`.trim()}
                  onClick={() => onEditLine(item)}
                >
                  <td className="status-cell">
                    <span className={`ic-status ${item.status}`}>{STATUS_LABEL[item.status]}</span>
                  </td>
                  <td className="desc-cell">{name}</td>
                  <td className="barcode-cell">{item.barcode}</td>
                  <td className="count-cell st-num-col" data-label="Exp">{item.expectedCount}</td>
                  <td className="count-cell st-num-col" data-label="Counted">{item.scannedCount}</td>
                  <td
                    className={`diff-cell st-num-col ${item.diff < 0 ? 'negative' : item.diff > 0 ? 'positive' : 'zero'}`}
                    data-label="Diff"
                  >
                    {formatSigned(item.diff)}
                    {item.bigVariance && item.status !== 'not_scanned' && (
                      <span className="st-big-flag" title="Big variance">
                        <AlertTriangle size={14} aria-hidden="true" /> Big
                      </span>
                    )}
                  </td>
                  <td className="edit-cell">
                    <button
                      type="button"
                      className="st-icon-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditLine(item);
                      }}
                      aria-label={`Edit count for ${name}`}
                    >
                      <Pencil size={18} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visible.length === 0 && (
          <p className="st-empty-filter">No lines match &ldquo;{query}&rdquo;</p>
        )}
      </div>
    </div>
  );
};

export default ICDocumentTable;
